import React from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useBranding } from "@/lib/branding";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Sparkles, ArrowRight, X, LayoutDashboard, Settings, Users, Wallet, KeyRound, ClipboardList, FilePenLine, Calendar, Newspaper } from "lucide-react";

const NAV_ITEMS = [
  { to: "/demo",              icon: LayoutDashboard, label: "Overview" },
  { to: "/demo/branding",     icon: Settings,        label: "Branding" },
  { to: "/demo/students",     icon: Users,           label: "Students" },
  { to: "/demo/payments",     icon: Wallet,          label: "Pending payments" },
  { to: "/demo/password-resets", icon: KeyRound,     label: "Password resets" },
  { to: "/demo/intake",       icon: ClipboardList,   label: "Intake forms" },
  { to: "/demo/amendments",   icon: FilePenLine,     label: "Amendments" },
  { to: "/demo/appointments", icon: Calendar,        label: "Appointments" },
  { to: "/demo/digest",       icon: Newspaper,       label: "Weekly digest" },
];

export function demoAction(label = "action") {
  toast.info(`This is a demo — sign up to actually run this ${label}.`);
}

export default function DemoLayout({ children }) {
  const { branding } = useBranding();
  const brand = branding?.brand_name || "DyNamic Tax";
  return (
    <div className="min-h-screen bg-background paper-texture flex flex-col">
      {/* Sticky demo banner */}
      <div className="bg-primary text-primary-foreground text-xs py-2 px-4 flex items-center justify-center gap-3 flex-wrap sticky top-0 z-40" data-testid="demo-banner">
        <Sparkles size={12} />
        <span className="font-medium tracking-widest uppercase">Demo mode</span>
        <span className="opacity-90 hidden sm:inline">— sample data, no real changes will be saved.</span>
        <Link to="/register" className="underline underline-offset-2 font-medium" data-testid="demo-signup-inline">Start 2-day trial<ArrowRight size={10} className="inline ml-0.5" /></Link>
        <Link to="/" className="ml-1 opacity-80 hover:opacity-100" aria-label="Exit demo" data-testid="demo-exit-btn"><X size={12} /></Link>
      </div>

      <div className="flex-1 flex">
        {/* Sidebar (demo) */}
        <aside className="hidden md:flex md:w-60 shrink-0 flex-col border-r border-border bg-card p-4 gap-1">
          <Link to="/" className="flex items-center gap-3 mb-6 pl-1">
            {branding?.logo_url ? <img src={branding.logo_url} alt="" className="h-9 w-9 object-contain" /> :
              <div className="h-9 w-9 rounded-md bg-primary text-primary-foreground grid place-items-center font-serif">{brand[0]}</div>}
            <div className="font-serif text-lg leading-tight">{brand}<div className="text-[9px] text-muted-foreground uppercase tracking-widest">Instructor demo</div></div>
          </Link>
          <div className="label-caps text-[9px] px-2 mb-1">Learn</div>
          <DemoNavLink icon={LayoutDashboard} label="Student dashboard" onClick={() => demoAction("preview")} />
          <div className="label-caps text-[9px] px-2 mt-3 mb-1">Admin</div>
          {NAV_ITEMS.map(item => <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/demo"}
              className={({ isActive }) => `flex items-center gap-2 px-2 py-2 rounded text-sm transition-colors ${isActive ? "bg-primary/10 text-primary" : "text-foreground hover:bg-secondary"}`}
              data-testid={`demo-nav-${item.to.split("/").pop() || "overview"}`}
            >
              <item.icon size={14} /> {item.label}
            </NavLink>
          )}
          <div className="mt-auto pt-4 border-t border-border">
            <Button asChild size="sm" className="w-full" data-testid="demo-sidebar-signup">
              <Link to="/register">Start free trial<ArrowRight size={12} className="ml-1" /></Link>
            </Button>
          </div>
        </aside>

        <main className="flex-1 min-w-0">
          {/* Mobile top nav bar */}
          <div className="md:hidden border-b border-border bg-card overflow-x-auto whitespace-nowrap px-3 py-2 flex gap-1">
            {NAV_ITEMS.map(item => (
              <NavLink key={item.to} to={item.to} end={item.to === "/demo"}
                className={({ isActive }) => `text-[11px] px-2 py-1 rounded ${isActive ? "bg-primary/10 text-primary" : "text-muted-foreground"}`}
              >
                {item.label}
              </NavLink>
            ))}
          </div>
          {children}
        </main>
      </div>
    </div>
  );
}

const DemoNavLink = ({ icon: Icon, label, onClick }) => (
  <button onClick={onClick} className="flex items-center gap-2 px-2 py-2 rounded text-sm text-muted-foreground hover:bg-secondary text-left">
    <Icon size={14} /> {label}
  </button>
);
